import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, MapPin, Truck, Package, FileText, AlertTriangle } from 'lucide-react';

// Gate Status Options - Updated per requirements
export const GATE_STATUS_OPTIONS = [
  { value: 'Received', label: 'Received' },
  { value: 'Partially Received', label: 'Partially Received' }
];

/**
 * Grey Receipt Form - LR-FIRST approach
 * 
 * FLOW:
 * 1. User selects LR (only "Issued for Delivery" status shown)
 * 2. Order and Bill are auto-derived from LR (NOT manual input)
 * 3. Auto-fill banner shows linked details
 * 4. Received Quantity validated against LR bales_in_lr
 * 5. Gate Status auto-set based on quantity (full = Received, partial = Partially Received)
 */
const GreyReceiptForm = ({ 
  orderId = null,        // Pre-selected order (from Order Details context)
  orderUnit = null,      // Unit from parent order
  bills = [],            // Bills for the selected order (for context only)
  lrs = [],              // LRs for the selected order (will be filtered)
  initialData = null,    // For edit mode
  onSuccess,             // Callback after successful submit
  onCancel,              // Cancel callback
  mode = 'create'        // 'create' or 'edit'
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingLRs, setLoadingLRs] = useState(false);
  const [availableLRs, setAvailableLRs] = useState([]);
  const [selectedOrderUnit, setSelectedOrderUnit] = useState(orderUnit);
  const [locations, setLocations] = useState([]);
  const [autofillInfo, setAutofillInfo] = useState(null);
  const [lrQuantity, setLrQuantity] = useState(null); // bales_in_lr from selected LR
  const [lrMaterialQuantity, setLrMaterialQuantity] = useState(null); // material_quantity from selected LR
  const [materialUnit, setMaterialUnit] = useState(null); // material unit from LR / quality default
  const [quantityError, setQuantityError] = useState('');
  const [materialQtyError, setMaterialQtyError] = useState('');
  const [gatePassFile, setGatePassFile] = useState(null);
  const [gatePassPreview, setGatePassPreview] = useState(initialData?.gate_pass_photo_url || null);
  const hasInitialized = useRef(false);
  
  const [formData, setFormData] = useState({
    order_id: orderId || '',
    bill_id: '',
    lr_id: '',
    location_id: '',
    received_date: new Date().toISOString().split('T')[0],
    received_quantity: '',
    received_material_quantity: '',
    verified_by: '',
    gate_status: 'Received',
    discrepancy_notes: ''
  });

  useEffect(() => {
    fetchLocations();
    if (mode === 'create') {
      fetchIssuedForDeliveryLRs();
    }
    if (initialData && !hasInitialized.current) {
      hasInitialized.current = true;
      setFormData({
        order_id: initialData.order_id || '',
        bill_id: initialData.bill_id || '',
        lr_id: initialData.lr_id || '',
        location_id: initialData.location_id || '',
        received_date: initialData.received_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        received_quantity: initialData.received_quantity || '',
        received_material_quantity: initialData.received_material_quantity ?? '',
        verified_by: initialData.verified_by || '',
        gate_status: initialData.gate_status || 'Received',
        discrepancy_notes: initialData.discrepancy_notes || ''
      });
      if (initialData.order_unit) {
        setSelectedOrderUnit(initialData.order_unit);
      }
      if (initialData.material_unit) {
        setMaterialUnit(initialData.material_unit);
      }
      if (initialData.lr_material_quantity != null) {
        setLrMaterialQuantity(initialData.lr_material_quantity);
      }
      if (initialData.lr_id) {
        loadAutofillData(initialData.lr_id);
      }
    }
  }, [initialData, mode]);

  // When orderId changes (embedded mode), filter LRs for that order
  useEffect(() => {
    if (orderId && mode === 'create') {
      fetchIssuedForDeliveryLRs(orderId);
    }
  }, [orderId, mode]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations', error);
    }
  };

  // Fetch LRs with "Issued for Delivery" status
  const fetchIssuedForDeliveryLRs = async (filterOrderId = null) => {
    setLoadingLRs(true);
    try {
      const url = filterOrderId 
        ? `/lr-reports/issued-for-delivery?order_id=${filterOrderId}`
        : '/lr-reports/issued-for-delivery';
      const response = await api.get(url);
      setAvailableLRs(response.data);
    } catch (error) {
      console.error('Failed to fetch issued LRs', error);
    }
    setLoadingLRs(false);
  };

  // Load autofill data from selected LR
  const loadAutofillData = async (lrId) => {
    if (!lrId || lrId === 'none') {
      setAutofillInfo(null);
      setLrQuantity(null);
      return;
    }
    
    try {
      const response = await api.get(`/lr-reports/${lrId}/autofill-data`);
      const data = response.data;
      
      if (data.suggested_values) {
        setAutofillInfo({
          order_id: data.suggested_values.order_id,
          order_number: data.suggested_values.order_number,
          bill_id: data.suggested_values.bill_id,
          bill_number: data.suggested_values.bill_number,
          lr_number: data.suggested_values.lr_number,
          supplier_name: data.suggested_values.supplier_name,
          quality_name: data.suggested_values.quality_name,
          item_name: data.suggested_values.item_name,
          transport_name: data.suggested_values.transport_name,
          bales_in_lr: data.suggested_values.bales_in_lr,
          material_quantity: data.suggested_values.material_quantity,
          material_unit: data.suggested_values.material_unit,
          order_unit: data.suggested_values.order_unit
        });
        setLrQuantity(data.suggested_values.bales_in_lr);
        setLrMaterialQuantity(data.suggested_values.material_quantity ?? null);
        setMaterialUnit(data.suggested_values.material_unit || null);
        setSelectedOrderUnit(data.suggested_values.order_unit);

        // Prefill a BLANK received material qty from the linked LR (historical correction).
        // Never overwrite a value the receipt already has.
        const lrMatQty = data.suggested_values.material_quantity;
        if (lrMatQty != null) {
          setFormData(prev => (
            (prev.received_material_quantity === '' || prev.received_material_quantity == null)
              ? { ...prev, received_material_quantity: lrMatQty }
              : prev
          ));
        }
      }
    } catch (error) {
      console.error('Failed to fetch LR autofill data', error);
    }
  };

  // Handle LR selection - auto-fill everything from LR
  const handleLRChange = async (lrId) => {
    setFormData(prev => ({ ...prev, lr_id: lrId }));
    setQuantityError('');
    setMaterialQtyError('');
    
    if (lrId && lrId !== 'none') {
      await loadAutofillData(lrId);
    } else {
      setAutofillInfo(null);
      setLrQuantity(null);
      setLrMaterialQuantity(null);
      setMaterialUnit(null);
    }
  };

  // Handle received material quantity change with validation
  const handleMaterialQtyChange = (value) => {
    setFormData(prev => ({ ...prev, received_material_quantity: value }));
    const qty = parseFloat(value);
    if (lrMaterialQuantity != null && !isNaN(qty) && qty > lrMaterialQuantity) {
      setMaterialQtyError(`Received material quantity (${qty}) cannot exceed LR material quantity (${lrMaterialQuantity})`);
    } else {
      setMaterialQtyError('');
    }
  };

  // Handle quantity change with validation and auto gate status
  const handleQuantityChange = (value) => {
    const qty = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, received_quantity: value }));
    
    // Validate against LR quantity
    if (lrQuantity && qty > lrQuantity) {
      setQuantityError(`Received quantity (${qty}) cannot exceed LR quantity (${lrQuantity})`);
    } else {
      setQuantityError('');
    }
    
    // Auto-set gate status based on quantity
    if (lrQuantity && qty > 0) {
      if (qty >= lrQuantity) {
        setFormData(prev => ({ ...prev, received_quantity: value, gate_status: 'Received' }));
      } else {
        setFormData(prev => ({ ...prev, received_quantity: value, gate_status: 'Partially Received' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: LR must be selected
    if (!formData.lr_id || formData.lr_id === 'none') {
      toast.error('Please select an LR');
      return;
    }
    
    // Validation: Quantity must be filled
    if (!formData.received_quantity) {
      toast.error('Please enter received quantity');
      return;
    }
    
    // Validation: Verified by must be filled
    if (!formData.verified_by) {
      toast.error('Please enter verified by');
      return;
    }
    
    // Validation: Quantity cannot exceed LR quantity
    const qty = parseFloat(formData.received_quantity);
    if (lrQuantity && qty > lrQuantity) {
      toast.error(`Received quantity (${qty}) cannot exceed LR quantity (${lrQuantity})`);
      return;
    }

    // Validation: Material quantity (required when LR has material qty; positive; within LR)
    const matQty = formData.received_material_quantity === '' ? null : parseFloat(formData.received_material_quantity);
    if (lrMaterialQuantity != null && (matQty === null || isNaN(matQty))) {
      toast.error('Please enter received material quantity');
      return;
    }
    if (matQty !== null && (isNaN(matQty) || matQty <= 0)) {
      toast.error('Received material quantity must be greater than 0');
      return;
    }
    if (lrMaterialQuantity != null && matQty != null && matQty > lrMaterialQuantity) {
      toast.error(`Received material quantity (${matQty}) cannot exceed LR material quantity (${lrMaterialQuantity})`);
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        order_id: autofillInfo?.order_id || formData.order_id,
        bill_id: autofillInfo?.bill_id || null,
        lr_id: formData.lr_id,
        location_id: formData.location_id && formData.location_id !== 'none' ? formData.location_id : null,
        received_date: formData.received_date,
        received_quantity: qty,
        received_material_quantity: matQty,
        material_unit: materialUnit,
        verified_by: formData.verified_by,
        gate_status: formData.gate_status,
        discrepancy_notes: formData.discrepancy_notes || null
      };

      if (mode === 'edit' && initialData?.receipt_id) {
        await api.put(`/grey-receipts/${initialData.receipt_id}`, payload);
        if (gatePassFile) {
          const formDataUpload = new FormData();
          formDataUpload.append('file', gatePassFile);
          await api.post(`/grey-receipts/${initialData.receipt_id}/upload-gate-pass-photo`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success('Receipt updated successfully!');
      } else {
        const res = await api.post('/grey-receipts', payload);
        if (gatePassFile && res.data?.receipt_id) {
          const formDataUpload = new FormData();
          formDataUpload.append('file', gatePassFile);
          await api.post(`/grey-receipts/${res.data.receipt_id}/upload-gate-pass-photo`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        toast.success(`Receipt added successfully! (${qty} ${selectedOrderUnit || 'units'} received)`);
      }
      
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${mode === 'edit' ? 'update' : 'add'} receipt`);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* STEP 1: LR Selection - PRIMARY SELECTOR */}
      <div>
        <Label htmlFor="lr_id" className="flex items-center gap-1 text-base font-semibold">
          <Truck className="h-4 w-4" />
          Select LR * <span className="text-xs font-normal text-slate-500">(Issued for Delivery only)</span>
        </Label>
        <Select
          value={formData.lr_id}
          onValueChange={handleLRChange}
          disabled={mode === 'edit'}
        >
          <SelectTrigger data-testid="receipt-lr-select" className="h-11 mt-1">
            <SelectValue placeholder={loadingLRs ? "Loading LRs..." : "Select LR to create receipt"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-- Select LR --</SelectItem>
            {availableLRs.length === 0 ? (
              <SelectItem value="no-lrs" disabled>
                {loadingLRs ? "Loading..." : "No LRs with 'Issued for Delivery' status"}
              </SelectItem>
            ) : (
              availableLRs.map((lr) => (
                <SelectItem key={lr.lr_id} value={lr.lr_id}>
                  {lr.lr_number_transport || lr.lr_number} - {lr.transport_name} ({lr.bales_in_lr} Bales, {lr.order_number})
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 mt-1">
          Only LRs with "Issued for Delivery" status. Order and Bill will be auto-filled.
        </p>
      </div>

      {/* STEP 2: Auto-fill Info Banner - Shows linked LR details */}
      {autofillInfo && (
        <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-testid="lr-autofill-banner">
          <div className="flex items-start gap-3">
            <Package className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-teal-800 text-sm">Linked from LR: {autofillInfo.lr_number}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-teal-600">Order:</span>
                  <span className="font-medium text-teal-800">{autofillInfo.order_number || '-'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-teal-600">Bill No:</span>
                  <span className="font-medium text-teal-800">{autofillInfo.bill_number || '-'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-teal-600">Transport:</span>
                  <span className="font-medium text-teal-800">{autofillInfo.transport_name || '-'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-teal-600">Bales:</span>
                  <span className="font-bold text-teal-800">{autofillInfo.bales_in_lr || '-'}</span>
                </div>
                {autofillInfo.material_quantity != null && (
                  <div className="flex items-center gap-1">
                    <span className="text-teal-600">Material Qty:</span>
                    <span className="font-bold text-teal-800">{autofillInfo.material_quantity} {autofillInfo.material_unit || ''}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <span className="text-teal-600">Item:</span>
                  <span className="font-medium text-teal-800">{autofillInfo.item_name || '-'}</span>
                </div>
                {autofillInfo.supplier_name && (
                  <div className="flex items-center gap-1">
                    <span className="text-teal-600">Supplier:</span>
                    <span className="font-medium text-teal-800">{autofillInfo.supplier_name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No LR selected message */}
      {!autofillInfo && formData.lr_id !== 'none' && formData.lr_id === '' && (
        <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg text-center text-slate-600 text-sm">
          Select an LR above to see linked Order and Bill details
        </div>
      )}

      {/* Location Selection */}
      <div>
        <Label htmlFor="location_id" className="flex items-center gap-1">
          <MapPin className="h-3 w-3" /> Location
        </Label>
        <Select
          value={formData.location_id}
          onValueChange={(value) => setFormData({ ...formData, location_id: value })}
        >
          <SelectTrigger data-testid="receipt-location-select" className="h-11">
            <SelectValue placeholder="Select receiving location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not Specified</SelectItem>
            {locations.map((loc) => (
              <SelectItem key={loc.location_id} value={loc.location_id}>
                {loc.location_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 mt-1">Factory/warehouse where goods are received</p>
      </div>

      <div>
        <Label htmlFor="received_date">Received Date *</Label>
        <Input
          id="received_date"
          data-testid="received-date-input"
          type="date"
          value={formData.received_date}
          onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
          required
          className="h-11"
        />
      </div>

      {/* Received Quantity with LR validation */}
      <div>
        <Label htmlFor="received_quantity" className="flex items-center gap-2">
          Received Quantity *
          {lrQuantity && (
            <span className="text-xs font-normal text-slate-500">
              (LR has {lrQuantity} {selectedOrderUnit || 'Bales'})
            </span>
          )}
        </Label>
        <Input
          id="received_quantity"
          data-testid="received-quantity-input"
          type="number"
          step="0.01"
          max={lrQuantity || undefined}
          value={formData.received_quantity}
          onChange={(e) => handleQuantityChange(e.target.value)}
          placeholder={lrQuantity ? `Max: ${lrQuantity}` : "Enter quantity"}
          required
          className={`h-11 ${quantityError ? 'border-red-500 focus:ring-red-500' : ''}`}
        />
        {quantityError && (
          <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>{quantityError}</span>
          </div>
        )}
        {!quantityError && lrQuantity && (
          <p className="text-xs text-slate-500 mt-1">
            Unit: {selectedOrderUnit || 'Bales'} • Max allowed: {lrQuantity}
          </p>
        )}
      </div>

      {/* Received Material Quantity with auto unit (shown when LR carries a material quantity) */}
      {(lrMaterialQuantity != null || mode === 'edit') && (
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label htmlFor="received_material_quantity" className="flex items-center gap-2">
              Received Material Quantity {lrMaterialQuantity != null && '*'}
              {lrMaterialQuantity != null && (
                <span className="text-xs font-normal text-slate-500">
                  (LR has {lrMaterialQuantity} {materialUnit || ''})
                </span>
              )}
            </Label>
            <Input
              id="received_material_quantity"
              data-testid="received-material-quantity-input"
              type="number"
              step="0.01"
              min="0"
              max={lrMaterialQuantity || undefined}
              value={formData.received_material_quantity}
              onChange={(e) => handleMaterialQtyChange(e.target.value)}
              placeholder={lrMaterialQuantity != null ? `Max: ${lrMaterialQuantity}` : 'Enter material quantity'}
              className={`h-11 ${materialQtyError ? 'border-red-500 focus:ring-red-500' : ''}`}
            />
            {materialQtyError && (
              <div className="flex items-center gap-1 mt-1 text-red-600 text-sm">
                <AlertTriangle className="h-4 w-4" />
                <span>{materialQtyError}</span>
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="receipt_material_unit">Unit</Label>
            <Input
              id="receipt_material_unit"
              data-testid="receipt-material-unit-display"
              value={materialUnit || ''}
              readOnly
              disabled
              className="h-11 bg-slate-100 text-slate-600 cursor-not-allowed"
              title="Auto-set from the linked LR / Quality default unit"
            />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="verified_by">Verified By *</Label>
        <Input
          id="verified_by"
          data-testid="verified-by-input"
          value={formData.verified_by}
          onChange={(e) => setFormData({ ...formData, verified_by: e.target.value })}
          placeholder="Inspector name"
          required
          className="h-11"
        />
      </div>

      {/* Gate Status - Auto-set based on quantity */}
      <div>
        <Label htmlFor="gate_status" className="flex items-center gap-2">
          Gate Status *
          {lrQuantity && formData.received_quantity && (
            <span className="text-xs font-normal text-slate-500">
              (Auto-set based on quantity)
            </span>
          )}
        </Label>
        <Select
          value={formData.gate_status}
          onValueChange={(value) => setFormData({ ...formData, gate_status: value })}
        >
          <SelectTrigger data-testid="gate-status-select" className="h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GATE_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 mt-1">
          {formData.gate_status === 'Received' 
            ? 'Full quantity received' 
            : 'Partial quantity received'}
        </p>
      </div>

      <div>
        <Label htmlFor="discrepancy_notes">Discrepancy Notes (Optional)</Label>
        <Textarea
          id="discrepancy_notes"
          data-testid="discrepancy-notes-input"
          value={formData.discrepancy_notes}
          onChange={(e) => setFormData({ ...formData, discrepancy_notes: e.target.value })}
          placeholder="Any quality issues or discrepancies..."
          rows={3}
        />
      </div>

      {/* Gate Pass Received Photo */}
      <div>
        <Label htmlFor="gate_pass_photo">Gate Pass Received Photo (Optional)</Label>
        <div className="mt-1">
          {gatePassPreview ? (
            <div className="space-y-2">
              <img
                src={gatePassFile ? URL.createObjectURL(gatePassFile) : `${process.env.REACT_APP_BACKEND_URL || ''}${gatePassPreview}`}
                alt="Gate Pass"
                className="max-h-32 rounded border border-slate-200"
              />
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    data-testid="gate-pass-photo-input"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setGatePassFile(file);
                        setGatePassPreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                  <span className="text-sm text-teal-600 hover:underline">Replace Photo</span>
                </label>
                <button
                  type="button"
                  className="text-sm text-red-500 hover:underline"
                  onClick={() => { setGatePassFile(null); setGatePassPreview(null); }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                data-testid="gate-pass-photo-input"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setGatePassFile(file);
                    setGatePassPreview(URL.createObjectURL(file));
                  }
                }}
              />
              <span className="text-sm text-slate-500">Tap to upload gate pass photo</span>
            </label>
          )}
        </div>
      </div>

      <div className="pt-4 flex gap-2">
        <Button
          data-testid="submit-receipt-btn"
          type="submit"
          disabled={loading || !!quantityError || !!materialQtyError || !formData.lr_id || formData.lr_id === 'none'}
          className="flex-1 bg-slate-900 hover:bg-slate-800 h-11"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'edit' ? 'Saving...' : 'Adding...'}
            </>
          ) : (
            mode === 'edit' ? 'Save Changes' : 'Add Receipt'
          )}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} className="h-11">
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};

export default GreyReceiptForm;
